(() => {
  const SELECTORS = {
    section: '[data-product-flavors]',
    grid: '[data-flavor-grid]',
    card: '[data-flavor-card]',
    template: '[data-flavor-template]',
    detail: '[data-flavor-detail]',
    detailBody: '[data-flavor-detail-body]',
    close: '[data-flavor-close]',
  };

  const SETTINGS = {
    scrollDuration: 300,
    contentTransitionDuration: 300,
  };

  class ProductFlavorGrid {
    constructor(section) {
      this.section = section;
      this.grid = section.querySelector(SELECTORS.grid);
      this.detail = section.querySelector(SELECTORS.detail);
      this.detailBody = section.querySelector(
        SELECTORS.detailBody
      );
      this.closeButton = section.querySelector(
        SELECTORS.close
      );

      this.cards = [];
      this.activeCard = null;
      this.activeRowTop = null;

      /*
       * 新しい操作が始まったときに、
       * 古い非同期処理を無効にするための番号です。
       */
      this.operationId = 0;

      /*
       * 実行中のスクロールアニメーションを保持します。
       */
      this.scrollAnimationFrame = null;

      this.abortController = new AbortController();

      if (
        !this.grid ||
        !this.detail ||
        !this.detailBody ||
        !this.closeButton
      ) {
        return;
      }

      this.refreshCards();
      this.bindEvents();
    }

    refreshCards() {
      this.cards = Array.from(
        this.grid.querySelectorAll(SELECTORS.card)
      );
    }

    bindEvents() {
      const { signal } = this.abortController;

      this.grid.addEventListener(
        'click',
        (event) => {
          const card = event.target.closest(
            SELECTORS.card
          );

          if (!card || !this.grid.contains(card)) {
            return;
          }

          this.open(card);
        },
        { signal }
      );

      this.closeButton.addEventListener(
        'click',
        () => {
          this.close({
            restoreFocus: true,
          });
        },
        { signal }
      );

      this.section.addEventListener(
        'keydown',
        (event) => {
          if (
            event.key !== 'Escape' ||
            this.detail.hidden
          ) {
            return;
          }

          event.preventDefault();

          this.close({
            restoreFocus: true,
          });
        },
        { signal }
      );

      window.addEventListener(
        'resize',
        this.debounce(() => {
          if (
            !this.activeCard ||
            this.detail.hidden
          ) {
            return;
          }

          /*
           * 列数が変わった場合は、選択中カードの
           * 新しい行末へ詳細パネルを移動します。
           */
          this.moveDetailAfterCardRow(
            this.activeCard
          );

          this.activeRowTop =
            this.getCardRowTop(this.activeCard);
        }, 150),
        { signal }
      );
    }

    async open(card) {
      const template = card.querySelector(
        SELECTORS.template
      );

      if (!template) return;

      /*
       * 実行中の古い処理とスクロールを停止します。
       */
      const currentOperation = this.startOperation();

      const clickedRowTop =
        this.getCardRowTop(card);

      const isDetailOpen = !this.detail.hidden;

      const isSameRow =
        isDetailOpen &&
        this.activeRowTop !== null &&
        this.isSamePosition(
          clickedRowTop,
          this.activeRowTop
        );

      this.updateActiveCard(card);

      /*
       * 同じ行のカードを選択した場合は、
       * 詳細パネルを移動せず内容だけ切り替えます。
       */
      if (isSameRow) {
        await this.hideDetailContent();

        if (!this.isCurrentOperation(currentOperation)) {
          return;
        }

        this.updateDetailContent(template);
        this.activeCard = card;
        this.activeRowTop =
          this.getCardRowTop(card);

        await this.showDetailContent();

        return;
      }

      /*
       * 別の行へ切り替える場合、
       * 現在表示中の詳細を先に消します。
       */
      if (isDetailOpen) {
        await this.hideDetailContent();

        if (!this.isCurrentOperation(currentOperation)) {
          return;
        }

        this.detail.hidden = true;
      }

      /*
       * 詳細が非表示の状態で、新しい行の直下へ移動します。
       * スクロール中に大きな詳細が表示されないため、
       * レイアウトの上下動を抑えられます。
       */
      this.moveDetailAfterCardRow(card);

      this.activeCard = card;
      this.activeRowTop =
        this.getCardRowTop(card);

      /*
       * まず選択したカードへスクロールします。
       */
      await this.scrollToCard(card);

      if (!this.isCurrentOperation(currentOperation)) {
        return;
      }

      /*
       * カードへ到着してから詳細を展開します。
       */
      this.updateDetailContent(template);
      this.detail.hidden = false;

      await this.showDetailContent();
    }

    async close({ restoreFocus = false } = {}) {
      if (this.detail.hidden) return;

      const previousCard = this.activeCard;

      if (!previousCard) {
        this.finishClose();
        return;
      }

      const currentOperation = this.startOperation();

      /*
       * 詳細はまだ表示したまま、
       * 選択中のカードへ戻ります。
       */
      await this.scrollToCard(previousCard);

      if (!this.isCurrentOperation(currentOperation)) {
        return;
      }

      /*
       * カードへ戻ったあとに詳細を消します。
       */
      await this.hideDetailContent();

      if (!this.isCurrentOperation(currentOperation)) {
        return;
      }

      this.finishClose();

      if (restoreFocus) {
        previousCard.focus({
          preventScroll: true,
        });
      }
    }

    finishClose() {
      this.detail.hidden = true;
      this.detailBody.classList.remove(
        'is-visible'
      );
      this.detailBody.replaceChildren();

      this.cards.forEach((card) => {
        card.setAttribute(
          'aria-expanded',
          'false'
        );
      });

      this.activeCard = null;
      this.activeRowTop = null;

      /*
       * 閉じた詳細パネルをGrid末尾へ戻します。
       */
      this.grid.appendChild(this.detail);
    }

    updateDetailContent(template) {
      const fragment =
        template.content.cloneNode(true);

      this.detailBody.replaceChildren(fragment);
    }

    updateActiveCard(activeCard) {
      this.cards.forEach((card) => {
        const isActive = card === activeCard;

        card.setAttribute(
          'aria-expanded',
          isActive ? 'true' : 'false'
        );
      });
    }

    async showDetailContent() {
      this.detailBody.classList.remove(
        'is-visible'
      );

      /*
       * hidden解除とDOM更新をブラウザへ反映してから
       * 表示クラスを追加します。
       */
      await this.nextFrame();
      await this.nextFrame();

      this.detailBody.classList.add(
        'is-visible'
      );
    }

    async hideDetailContent() {
      if (
        !this.detailBody.classList.contains(
          'is-visible'
        )
      ) {
        return;
      }

      this.detailBody.classList.remove(
        'is-visible'
      );

      if (this.prefersReducedMotion()) {
        return;
      }

      await this.wait(
        SETTINGS.contentTransitionDuration
      );
    }

    moveDetailAfterCardRow(card) {
      const rowLastCard =
        this.getRowLastCard(card);

      if (!rowLastCard) return;

      rowLastCard.insertAdjacentElement(
        'afterend',
        this.detail
      );
    }

    getRowLastCard(targetCard) {
      this.refreshCards();

      const targetRowTop =
        this.getCardRowTop(targetCard);

      const cardsInSameRow =
        this.cards.filter((card) => {
          return this.isSamePosition(
            this.getCardRowTop(card),
            targetRowTop
          );
        });

      return cardsInSameRow.at(-1) || targetCard;
    }

    getCardRowTop(card) {
      const gridRect =
        this.grid.getBoundingClientRect();

      const cardRect =
        card.getBoundingClientRect();

      return Math.round(
        cardRect.top - gridRect.top
      );
    }

    isSamePosition(positionA, positionB) {
      return Math.abs(
        positionA - positionB
      ) <= 2;
    }

    scrollToCard(card) {
      this.cancelScrollAnimation();

      if (!card) {
        return Promise.resolve();
      }

      const scrollMarginTop = Number.parseFloat(
        window
          .getComputedStyle(card)
          .scrollMarginTop
      ) || 0;

      const cardRect =
        card.getBoundingClientRect();

      const startY = window.scrollY;

      const targetY = Math.max(
        0,
        startY +
          cardRect.top -
          scrollMarginTop
      );

      const distance = targetY - startY;

      /*
       * ほとんど同じ位置ならアニメーションしません。
       */
      if (Math.abs(distance) < 2) {
        return Promise.resolve();
      }

      if (this.prefersReducedMotion()) {
        window.scrollTo({
          top: targetY,
          behavior: 'auto',
        });

        return Promise.resolve();
      }

      return new Promise((resolve) => {
        const startTime = performance.now();

        const step = (currentTime) => {
          const elapsed =
            currentTime - startTime;

          const progress = Math.min(
            elapsed /
              SETTINGS.scrollDuration,
            1
          );

          const easedProgress =
            this.easeInOutCubic(progress);

          window.scrollTo({
            top:
              startY +
              distance * easedProgress,
            behavior: 'auto',
          });

          if (progress < 1) {
            this.scrollAnimationFrame =
              window.requestAnimationFrame(
                step
              );

            return;
          }

          this.scrollAnimationFrame = null;
          resolve();
        };

        this.scrollAnimationFrame =
          window.requestAnimationFrame(step);
      });
    }

    cancelScrollAnimation() {
      if (
        this.scrollAnimationFrame === null
      ) {
        return;
      }

      window.cancelAnimationFrame(
        this.scrollAnimationFrame
      );

      this.scrollAnimationFrame = null;
    }

    easeInOutCubic(progress) {
      return progress < 0.5
        ? 4 * progress * progress * progress
        : 1 -
            Math.pow(
              -2 * progress + 2,
              3
            ) /
              2;
    }

    prefersReducedMotion() {
      return window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;
    }

    startOperation() {
      this.operationId += 1;
      this.cancelScrollAnimation();

      return this.operationId;
    }

    isCurrentOperation(operationId) {
      return operationId === this.operationId;
    }

    nextFrame() {
      return new Promise((resolve) => {
        window.requestAnimationFrame(() => {
          resolve();
        });
      });
    }

    wait(duration) {
      return new Promise((resolve) => {
        window.setTimeout(resolve, duration);
      });
    }

    debounce(callback, delay) {
      let timerId;

      return (...args) => {
        window.clearTimeout(timerId);

        timerId = window.setTimeout(() => {
          callback(...args);
        }, delay);
      };
    }

    destroy() {
      this.operationId += 1;
      this.cancelScrollAnimation();
      this.abortController.abort();
    }
  }

  const instances = new Map();

  const initializeSection = (section) => {
    if (
      !section ||
      instances.has(section)
    ) {
      return;
    }

    const instance =
      new ProductFlavorGrid(section);

    instances.set(section, instance);
  };

  const destroySection = (section) => {
    const instance = instances.get(section);

    if (!instance) return;

    instance.destroy();
    instances.delete(section);
  };

  const initializeAll = (
    root = document
  ) => {
    if (root.matches?.(SELECTORS.section)) {
      initializeSection(root);
    }

    root
      .querySelectorAll(SELECTORS.section)
      .forEach((section) => {
        initializeSection(section);
      });
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initializeAll();
      }
    );
  } else {
    initializeAll();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initializeAll(event.target);
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      const section =
        event.target.matches?.(
          SELECTORS.section
        )
          ? event.target
          : event.target.querySelector(
              SELECTORS.section
            );

      if (section) {
        destroySection(section);
      }
    }
  );

  document.addEventListener(
    'shopify:block:select',
    (event) => {
      const card =
        event.target.matches?.(
          SELECTORS.card
        )
          ? event.target
          : event.target.querySelector(
              SELECTORS.card
            );

      if (!card) return;

      const section = card.closest(
        SELECTORS.section
      );

      const instance =
        instances.get(section);

      instance?.open(card);
    }
  );
})();